import { IRegister, Register } from '@core/entities/register/Register'
import { AuthGroup, IAuthGroup } from '@core/entities/Groups/AuthGroups'
import { IUser, User } from '@core/entities/user'
import { IOrganisation, Organisation } from '@core/entities/organisation'
import {
  CompanyOnboardingSubmission,
  ICompanyOnboardingSubmission,
} from '@core/entities/notifications/CompanyOnboardingSubmission'
import {
  CompanyComplianceNotification,
  ICompanyComplianceNotification,
} from '@core/entities/notifications/CompanyComplianceNotification'
import {
  CompetencyAssignmentNotification,
  ICompetencyAssignmentNotification,
} from '@core/entities/notifications/CompetencyAssignmentNotification'
import {
  IRegisterItemTargetResult,
  RegisterItemTargetResult,
} from '@core/entities/notifications/RegisterItemTargetResult'
import {
  IPermitTargetResult,
  PermitTargetResult,
} from '@core/entities/notifications/PermitTargetResult'
import {
  ChecklistTargetResult,
  IChecklistTargetResult,
} from '@core/entities/notifications/ChecklistTargetResult'
import {
  ITaskTargetResult,
  TaskTargetResult,
} from '@core/entities/notifications/TaskTargetResult'
import {
  IProjectTargetResult,
  ProjectTargetResult,
} from '@core/entities/notifications/ProjectTargetResult'
import {
  IObservationTargetResult,
  ObservationTargetResult,
} from '@core/entities/notifications/ObservationTargetResult'

export type AppNotificationGetStatus = 'ALL' | 'READ' | 'UNREAD'

// ------- Target Result -------
export const appNotificationTargetResultClassMap = {
  USER: User,
  ORGANISATION: Organisation,
  PERMIT: PermitTargetResult,
  CHECKLIST: ChecklistTargetResult,
  TASK: TaskTargetResult,
  PROJECT: ProjectTargetResult,
  GROUP: AuthGroup,
  REGISTER: Register,
  REGISTER_ITEM: RegisterItemTargetResult,
  COMPANY_ONBOARDING: CompanyOnboardingSubmission,
  COMPANY_COMPLIANCE: CompanyComplianceNotification,
  OBSERVATION: ObservationTargetResult,
  COMPETENCY: CompetencyAssignmentNotification,
} as const

type AppNotificationTargetResultInterfaceMap = {
  USER: IUser
  ORGANISATION: IOrganisation
  PERMIT: IPermitTargetResult
  CHECKLIST: IChecklistTargetResult
  TASK: ITaskTargetResult
  PROJECT: IProjectTargetResult
  GROUP: IAuthGroup
  REGISTER: IRegister
  REGISTER_ITEM: IRegisterItemTargetResult
  COMPANY_ONBOARDING: ICompanyOnboardingSubmission
  COMPANY_COMPLIANCE: ICompanyComplianceNotification
  OBSERVATION: IObservationTargetResult
  COMPETENCY: ICompetencyAssignmentNotification
}

export type AppNotificationReferenceObject =
  keyof typeof appNotificationTargetResultClassMap

export type IAppNotificationTargetResult<
  T extends AppNotificationReferenceObject,
> = AppNotificationTargetResultInterfaceMap[T]

type AppNotificationTargetResultClassMap = {
  [K in AppNotificationReferenceObject]: InstanceType<
    typeof appNotificationTargetResultClassMap[K]
  >
}

export type AppNotificationTargetResult<
  T extends AppNotificationReferenceObject,
> = AppNotificationTargetResultClassMap[T]

// ------- Action -------
export type AppNotificationAction =
  | 'SENT_TO_PERMIT_HOLDER'
  | 'REQUESTED'
  | 'ISSUED'
  | 'OPENED'
  | 'EXTENSION_REQUESTED'
  | 'EXTENSION_APPROVED'
  | 'EXTENSION_REJECTED'
  | 'DISCARDED'
  | 'REJECTED'
  | 'SIGNED'
  | 'AWAITING_FOR_FINAL_SIGN_OFF'
  | 'CLOSED_ADMIN'
  | 'CLOSED_USER'
  | 'CANCELLED'
  | 'COUNTDOWN_FINISHED'
  | 'EXPIRED'
  | 'ABOUT_TO_EXPIRE'
  | 'REMIND_POST_PERMIT_CHECKS'
  | 'CHANGES_REQUESTED'
  | 'CHANGED_ADDRESSED'
  | 'PARTICIPANT_ADDED'
  | 'PARTICIPANT_ADDED_FOR_ACCEPTANCE'
  | 'BRIEFING_ACCEPTED'
  | 'BRIEFING_DECLINED'
  | 'TRANSFER_INITIATED'
  | 'TRANSFER_REQUESTED'
  | 'TRANSFER_ACCEPTED'
  | 'TRANSFER_REJECTED'
  | 'TRANSFER_CANCELLED'
  | 'SUSPENDED'
  | 'RESUMED'
  | 'SUSPENSION_RESUME_REQUESTED'
  | 'SUSPENSION_RESUME_REQUEST_APPROVED'
  | 'SUSPENSION_RESUME_REQUEST_DECLINED'
  | 'REGISTER_ITEM_CREATED'
  | 'REGISTER_ITEM_UPDATED'
  | 'REGISTER_ITEM_SCHEDULED_INSPECTION_CREATED'
  | 'REGISTER_ITEM_SCHEDULED_INSPECTION_UPDATED'
  | 'REGISTER_ITEM_INSPECTION_OVERDUE'
  | 'COMMENT_ADDED'
  | 'TASK_ASSIGNED'
  | 'TASK_UPDATED'
  | 'TASK_COMPLETED'
  | 'TASK_CANCELLED'
  | 'TASK_DUE_DATE_REMINDER_SENT'
  | 'CHECKLIST_COMPLETED'
  | 'COMPANY_ONBOARDING_FORM_SUBMITTED_REVIEWER'
  | 'COMPANY_COMPLIANCE_DOCUMENT_UPLOADED_REVIEWER'
  | 'COMPANY_COMPLIANCE_DOCUMENT_REJECTED'
  | 'COMPANY_COMPLIANCE_COMPANY_ACTIVATED'
  | 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRING_28_DAYS'
  | 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRING_7_DAYS'
  | 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRED_COMPANY_ADMIN'
  | 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRED_REVIEWER'
  | 'OBSERVATION_RESPONSES_READY'
  | 'OBSERVATION_ASSIGNED'
  | 'OBSERVATION_CLOSED'
  | 'OBSERVATION_REJECTED'
  | 'COMPETENCY_TEMPLATE_ACTIVATED'
  | 'COMPETENCY_EXPIRING_30_DAYS'
  | 'COMPETENCY_EXPIRING_7_DAYS'
  | 'COMPETENCY_EXPIRED'
  | 'COMPETENCY_TEMPLATE_VERSION_PUBLISHED'
